import {
  DestinationType,
  FiatToken,
  mapFiatToDestination,
  OnChainTokenSymbol,
  QuoteError,
  QuoteResponse,
  RampDirection
} from "@vortexfi/shared";
import Big from "big.js";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { parseBig } from "../../helpers/numbers";
import { QuoteService } from "../../services/api";

interface QuoteParams {
  inputAmount?: Big;
  onChainToken: OnChainTokenSymbol;
  fiatToken: FiatToken;
  selectedNetwork: DestinationType;
  rampType: RampDirection;
  apiKey?: string;
  partnerId?: string;
}

interface QuotePayload {
  rampType: RampDirection;
  fromDestination: DestinationType;
  toDestination: DestinationType;
  inputAmount: string;
  inputCurrency: OnChainTokenSymbol | FiatToken;
  outputCurrency: OnChainTokenSymbol | FiatToken;
}

interface QuoteActions {
  actions: {
    fetchQuote: (params: QuoteParams) => Promise<void>;
    forceSetQuote: (quote: QuoteResponse) => void;
    reset: () => void;
  };
}

interface QuoteState {
  quote: QuoteResponse | undefined;
  loading: boolean;
  error: string | null; // This is either the error message or the key of the translation
  outputAmount: Big | undefined;
  exchangeRate: number;
}

const friendlyErrorMessages: Record<QuoteError, string> = {
  // Validation errors - show specific messages
  [QuoteError.MissingRequiredFields]: "pages.swap.error.missingFields",
  [QuoteError.MissingToField]: "pages.swap.error.missingToField",
  [QuoteError.MissingFromField]: "pages.swap.error.missingFromField",
  [QuoteError.InvalidRampType]: "pages.swap.error.invalidRampType",
  [QuoteError.InvalidNetworks]: "pages.swap.error.invalidNetworks",
  [QuoteError.QuoteNotFound]: "pages.swap.error.quoteNotFound",
  [QuoteError.AssetHubNotSupportedForAlfredPay]: "pages.swap.error.assetHubNotSupportedForAlfredPay",

  // Amount too low - suggest larger amount
  [QuoteError.InputAmountTooLowToCoverFees]: "pages.swap.error.tryLargerAmount",
  [QuoteError.InputAmountForSwapMustBeGreaterThanZero]: "pages.swap.error.tryLargerAmount",
  [QuoteError.InputAmountTooLow]: "pages.swap.error.tryLargerAmount",
  [QuoteError.InputAmountTooLowToCoverCalculatedFees]: "pages.swap.error.tryLargerAmount",
  [QuoteError.BelowLowerLimitSell]: QuoteError.BelowLowerLimitSell, // We leave this as-is, as the replacement string depends on the context
  [QuoteError.BelowLowerLimitBuy]: QuoteError.BelowLowerLimitBuy, // We leave this as-is, as the replacement string depends on the context
  [QuoteError.LowLiquidity]: "pages.swap.error.lowLiquidity",
  // Calculation failures - suggest different amount
  [QuoteError.UnableToGetPendulumTokenDetails]: "pages.swap.error.tryDifferentAmount",
  [QuoteError.FailedToCalculateQuote]: "pages.swap.error.tryDifferentAmount",
  [QuoteError.FailedToCalculatePreNablaDeductibleFees]: "pages.swap.error.tryDifferentAmount",
  [QuoteError.FailedToCalculateFeeComponents]: "pages.swap.error.tryDifferentAmount",
  [QuoteError.UnsupportedCurrency]: "pages.swap.error.unsupportedCurrency"
};

function getFriendlyErrorMessage(error: unknown) {
  if (error instanceof Error) {
    const friendlyErrorMessage = friendlyErrorMessages[error.message as QuoteError];
    return friendlyErrorMessage || error.message;
  }

  return "pages.swap.error.fetchingQuote";
}

/**
 * Creates a quote payload based on ramp parameters
 * @param params Quote parameters
 * @returns Quote payload for API request
 */
const createQuotePayload = (params: QuoteParams): QuotePayload => {
  const { inputAmount, onChainToken, fiatToken, selectedNetwork, rampType } = params;
  const fiatDestination = mapFiatToDestination(fiatToken);
  const inputAmountStr = inputAmount?.toString() || "0";

  const payloadMap: Record<RampDirection, QuotePayload> = {
    [RampDirection.SELL]: {
      fromDestination: selectedNetwork,
      inputAmount: inputAmountStr,
      inputCurrency: onChainToken,
      outputCurrency: fiatToken,
      rampType: RampDirection.SELL,
      toDestination: fiatDestination
    },
    [RampDirection.BUY]: {
      fromDestination: fiatDestination,
      inputAmount: inputAmountStr,
      inputCurrency: fiatToken,
      outputCurrency: onChainToken,
      rampType: RampDirection.BUY,
      toDestination: selectedNetwork
    }
  };

  return payloadMap[rampType];
};

/**
 * Calculates exchange rate and output amount from quote response
 * @param quoteResponse The API response
 * @returns Object containing output amount and exchange rate
 */
const processQuoteResponse = (quoteResponse: QuoteResponse) => {
  const outputAmount = parseBig(quoteResponse.outputAmount);
  // Calculate exchange rate safely using Big division, converting to Number at the end
  const inputAmount = parseBig(quoteResponse.inputAmount);
  const exchangeRate = inputAmount.eq(0) ? 0 : Number(outputAmount.div(inputAmount));

  return { exchangeRate, outputAmount };
};

const DEFAULT_QUOTE_STORE_VALUES: QuoteState = {
  error: null,
  exchangeRate: 0,
  loading: false,
  outputAmount: undefined,
  quote: undefined
};

export const useQuoteStore = create<QuoteState & QuoteActions>()(
  persist(
    (set, get) => ({
      ...DEFAULT_QUOTE_STORE_VALUES,
      actions: {
        fetchQuote: async (params: QuoteParams) => {
          const { inputAmount, partnerId, apiKey } = params;

          if (!inputAmount || inputAmount.eq(0)) {
            set({ error: "pages.swap.error.invalidInputAmount", loading: false, outputAmount: Big(0), quote: undefined });
            return;
          }

          set({ error: null, loading: true });

          try {
            const quotePayload = createQuotePayload(params);

            const quoteResponse = await QuoteService.createQuote(
              quotePayload.rampType,
              quotePayload.fromDestination,
              quotePayload.toDestination,
              quotePayload.inputAmount,
              quotePayload.inputCurrency,
              quotePayload.outputCurrency,
              apiKey,
              partnerId
            );

            const { outputAmount, exchangeRate } = processQuoteResponse(quoteResponse);

            set({
              exchangeRate,
              loading: false,
              outputAmount,
              quote: quoteResponse
            });
          } catch (error) {
            set({
              error: getFriendlyErrorMessage(error),
              loading: false,
              outputAmount: undefined,
              quote: undefined
            });
          }
        },
        forceSetQuote: (quote: QuoteResponse) => {
          const { outputAmount, exchangeRate } = processQuoteResponse(quote);
          set({ exchangeRate, loading: false, outputAmount, quote });
        },
        reset: () => {
          set({
            error: null,
            exchangeRate: 0,
            loading: false,
            outputAmount: undefined,
            quote: undefined
          });
        }
      },
      error: null,
      loading: false,
      providedQuoteId: undefined,
      quote: undefined
    }),
    {
      name: "useQuoteStore",
      partialize: state => ({
        error: state.error,
        loading: state.loading,
        quote: state.quote
      })
    }
  )
);

export const useQuoteOutputAmount = () => useQuoteStore(state => state.outputAmount);
export const useQuoteExchangeRate = () => useQuoteStore(state => state.exchangeRate);
export const useQuoteLoading = () => useQuoteStore(state => state.loading);
export const useQuoteError = () => useQuoteStore(state => state.error);
export const useQuote = () => useQuoteStore(state => state.quote);

export const useQuoteActions = () => useQuoteStore(state => state.actions);
